/**
 * File processing utilities for uploads
 * Handles file metadata, MIME types, and filesystem operations
 */

import mime from 'mime';

/**
 * Ensures a file has a proper MIME type
 * Falls back to detection based on file extension if type is empty
 *
 * @param {File} file - File object to process
 * @returns {File} File with guaranteed MIME type
 */
export function ensureFileMimeType(file) {
  if (file.type === '') {
    const extension = file.name.substring(file.name.lastIndexOf('.') + 1);
    const detectedType = mime.getType(extension) ?? '';

    Object.defineProperty(file, 'type', {
      value: detectedType,
    });
  }

  return file;
}

/**
 * Normalizes the webkitRelativePath property
 * Ensures it's set and properly formatted
 *
 * @param {File} file - File object to process
 * @param {string} fullPath - Full path from filesystem
 * @returns {File} File with normalized path
 */
export function normalizeWebkitRelativePath(file, fullPath) {
  let filePath = fullPath;

  if (filePath.startsWith('/')) {
    filePath = filePath.replace('/', '');
  }

  Object.defineProperty(file, 'webkitRelativePath', {
    get() {
      return filePath;
    },
  });

  return file;
}

/**
 * Processes a file to ensure it has all required properties
 * - Ensures MIME type is set
 * - Normalizes paths
 *
 * @param {File} file - File to process
 * @returns {File} Processed file
 */
export function processFile(file) {
  let processedFile = ensureFileMimeType(file);

  // If webkitRelativePath property doesn't exist or is just the filename,
  // normalize it to empty string to avoid creating unnecessary subdirectories
  if (!file.webkitRelativePath || file.webkitRelativePath.indexOf('/') === -1) {
    Object.defineProperty(processedFile, 'webkitRelativePath', {
      get() {
        return '';
      },
    });
  }

  return processedFile;
}

/**
 * Reads all entries from a directory recursively
 * Handles FileSystemDirectoryEntry and FileSystemFileEntry
 *
 * @param {FileSystemEntry} entry - Directory or file entry
 * @param {string} fullPath - Current path in the directory structure
 * @returns {Promise<File[]>} Array of files found
 */
export async function readFilesystemEntry(entry, fullPath) {
  return new Promise((resolve) => {
    // Handle file entry
    if (entry.isFile) {
      entry.file((file) => {
        let filePath = `${fullPath}/${file.name}`;
        if (filePath.startsWith('/')) {
          filePath = filePath.replace('/', '');
        }

        const processedFile = normalizeWebkitRelativePath(file, filePath);
        const fileWithMime = ensureFileMimeType(processedFile);

        resolve([fileWithMime]);
      });
      return;
    }

    // Handle directory entry
    if (entry.isDirectory) {
      const dirReader = entry.createReader();
      let filesArray = [];

      /**
       * Recursive function to read all entries in batches
       * DirectoryReader.readEntries() returns entries in batches
       */
      const readAllEntries = async () => {
        dirReader.readEntries(async (entries) => {
          if (entries.length > 0) {
            // Process this batch of entries
            for (const subEntry of entries) {
              const subFiles = await readFilesystemEntry(subEntry, entry.fullPath);
              filesArray = filesArray.concat(subFiles);
            }
            // Read the next batch
            await readAllEntries();
          } else {
            // No more entries, resolve with the complete array
            resolve(filesArray);
          }
        });
      };

      // Start reading entries
      readAllEntries();
    }
  });
}

/**
 * Processes drag and drop items from DataTransfer
 * Handles both files and directories
 *
 * @param {DataTransfer} dataTransfer - DataTransfer object from drop event
 * @returns {Promise<File[]>} Array of processed files
 */
export async function processDroppedItems(dataTransfer) {
  const dropItems = [];

  // Extract filesystem entries from dropped items
  if (dataTransfer && dataTransfer.items && dataTransfer.items.length > 0) {
    for (let i = 0; i < dataTransfer.items.length; i++) {
      const item = dataTransfer.items[i];

      // Try getAsEntry first (standard), then webkitGetAsEntry (fallback)
      let entry;
      if ('getAsEntry' in item && typeof item.getAsEntry === 'function') {
        entry = item.getAsEntry();
      } else {
        entry = item.webkitGetAsEntry();
      }

      if (entry) {
        dropItems.push(entry);
      }
    }
  }

  // Read all files from the dropped items
  let filesList = [];
  for (const dropItem of dropItems) {
    if (dropItem) {
      const foundFiles = await readFilesystemEntry(dropItem, '');
      filesList = filesList.concat(foundFiles);
    }
  }

  return filesList;
}

/**
 * Generates a unique request ID for tracking upload sessions
 * Used to correlate multiple uploads and folder creations
 *
 * @returns {string} Unique request ID
 */
export function generateRequestId() {
  const timestamp = new Date().getTime();
  const randomNum = Math.floor(Math.random() * 1000000);
  return `${timestamp}-${randomNum}`;
}
